import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import styled from 'styled-components';
import Cookies from 'js-cookie';
import storage from "utils/Storage";
import socket from "utils/Socket";
import { Event, wait, decode, encode, sendNotfication, RECAPTCHA, API } from 'utils/Helper';
import C from 'utils/Constant';
import { handleGoogleLogin, GoogleLoginButton } from './GoogleLoginButton';
import { startAuthentication } from '@simplewebauthn/browser';

import { Eye, EyeOff, Fingerprint } from "lucide-react";
import CustomLoader from "../../components/common/CustomLoader";
import { Link } from 'react-router-dom';
import ReCAPTCHA from "react-google-recaptcha";
import { showAccountBlockedModal } from "utils/blockModalHelper";



const StyledModal = styled(Modal)`
  .modal-dialog {
    margin: auto !important;
    @media(min-width:640px){
      max-width: 480px !important; /* Compact modal max width */
    }
  }
  .modal-content {
    background-color: #1A2C38; /* Stake card background */
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: white;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    box-shadow: 0 10px 40px rgba(0,0,0,0.5);
    
    /* Scroller settings exactly like Stake */
    height: fit-content !important;
    max-height: 85vh !important;
    overflow-y: auto !important;
    
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #2F4553;
      border-radius: 4px;
    }
  }

  /* Force equal padding on top and bottom of the modal container */
  &.modal {
    padding-top: 5vh !important;
    padding-bottom: 5vh !important;
  }

    @media(max-width:340px){
      margin: 5px !important;
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: #9CA3AF;
  font-size: 24px;
  cursor: pointer;
  z-index: 10;
  line-height: 1;

  &:hover {
    color: white;
  }
`;

const ModalBody = styled(Modal.Body)`
  padding: 32px;
  position: relative;
  @media(max-width:400px){
    padding: 24px;
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px; /* Stake spacing */
`;

const StyledInput = styled.input`
  background-color: #0F212E; /* Stake input background */
  border: 1px solid #2F4553;
  border-radius: 6px;
  color: white;
  padding: 0 16px;
  height: 48px;
  font-size: 14px;
  width: 100%;
  padding-right: 35px;
  transition: all 0.2s ease-in-out;
  
  &::placeholder {
    color: #557086;
  }
  
  &:focus {
    outline: none;
    border-color: #1fdf71;
    background-color: #0F212E;
  }
  
  /* Fix browser autofill */
  &:-webkit-autofill,
  &:-webkit-autofill:hover, 
  &:-webkit-autofill:focus, 
  &:-webkit-autofill:active{
    -webkit-box-shadow: 0 0 0 30px #0F212E inset !important;
    -webkit-text-fill-color: white !important;
    transition: background-color 5000s ease-in-out 0s;
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #9CA3AF;
  margin-bottom: 6px;
  text-transform: capitalize;
`;

const FieldContainer = styled.div`
  margin-bottom: 12px;
  width: 100%;
`;

const InputContainer = styled.div`
  display: flex;
  position: relative;
  width: 100%;
`;

const EyeIcon = styled.button`
  position: absolute;
  right: 10px; /* Adjust the position */
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #9CA3AF;
`;

const CaptchaRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
`;

const CaptchaCode = styled.div`
  min-width: 110px;
  background: #111313;
  border: 1px solid #3A4142;
  border-radius: 7px;
  color: #83EA77;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  letter-spacing: 2px;
  user-select: none;
`;

const RefreshCaptchaButton = styled.button`
  background-color: #1E2121;
  border: 1px solid #3A4142;
  border-radius: 7px;
  color: #B3BEC1;
  padding: 0 10px;
  cursor: pointer;
  white-space: nowrap;
`;

const StyledButton = styled.button`
  background-image: linear-gradient(90deg, #1fdf71, #84e056);
  box-shadow: rgba(31, 223, 113, 0.4) 0px 4px 14px 0px, rgba(255, 255, 255, 0.2) 0px 1px 0px inset;
  color: #0b2212;
  border: none;
  border-radius: 6px;
  height: 48px;
  font-weight: 700;
  cursor: pointer;
  width: 100%;
  font-size: 14px;
  margin-top: 5px;
  transition: all 0.3s ease;

  &:hover {
    background-image: linear-gradient(90deg, #1bca66, #74cc4b);
  }
`;

const StakeSocialButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 48px;
  min-height: 48px;
  padding: 12px 16px;
  border-radius: 6px;
  background-color: rgba(255, 255, 255, 0.05);
  border: none;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const StakeDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: #9CA3AF;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const SocialLoginContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
`;

const SocialLoginButton = styled.button`
  background-color: #2b2f33;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const OtpModalStyled = styled(Modal)`
  .modal-dialog {
    max-width: 320px;
    margin: auto;
    margin-top: 15vh;
  }
  .modal-content {
    background-color: #1A2C38;
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 10px 40px rgba(0,0,0,0.5);
  }
`;

const VerifyButton = styled.button`
  flex: 1;
  padding: 12px;
  background-color: #1fdf71;
  border: none;
  color: #0b2212;
  font-weight: 700;
  border-radius: 4px;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.9; }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px;
  background: transparent;
  border: 1px solid #2F4553;
  color: white;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: rgba(255, 255, 255, 0.05); }
`;

const OtpButton = styled.button`
  padding: 0 16px;
  height: 48px;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid #2F4553;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  min-width: 110px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;


const StyledSignInButton = styled.div`
  font-size: 16px;
  color:white !important;
  background-color: transparent;
  border: none;
  border-radius: 5px;
  padding: 8px 13px;
  font-weight: bold;
  cursor: pointer;
  white-space: nowrap;
`;

const Labeltext = styled.div`
color:#696F79;
font-size:14px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  justify-content:center;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 7px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
`;

const SignInButton = styled(Button)`
  background-image: linear-gradient(90deg, rgb(36, 238, 137), rgb(159, 232, 113));
  box-shadow: rgba(35, 238, 136, 0.3) 0px 0px 12px, rgb(29, 202, 106) 0px -2px inset;
  color: black;
  border: none;
  width: 100%;
  max-width: 100%;
  max-height:100%;
  margin-top:10px;

  &:hover {
    background-color: #7c3aed;
  }
`;

const SignUpButton = styled.div`
  color: rgb(36 238 137);
  background:transparent;
  width: 100%;
`;

const TwoFAModal = styled(Modal)`
    // Add any custom styles you want here
    .modal-content {
    background-color: #1e2124;
    color: white;
    border-radius: 18px;
    overflow: hidden;
  }
`;

const CloseButtonNew = styled.div`
position:absolute;
top:10px;
right:10px;
font-size:30px;
display:none;

@media(max-width:640px){
display:block;
}
`


const Login = (props) => {
  const [show, setShow] = useState(props.show || false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [effect, setEffect] = useState('zoomIn');
  const [show2faModal, setShow2faModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authConfig, setAuthConfig] = useState(null);
  const [otpValue, setOtpValue] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otpSentTo, setOtpSentTo] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('google');
  const recaptchaRef = React.useRef(null);

  const handlePasskeyLogin = async () => {
    setIsLoading(true);
    try {
      const optionsRes = await fetch(`${API}/passkeys/generate-authentication`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const optionsData = await optionsRes.json();
      
      if (!optionsData.success) {
        throw new Error(optionsData.error || 'Failed to generate options');
      }

      let asseResp;
      try {
        asseResp = await startAuthentication(optionsData.options);
      } catch (error) {
        if (error.name === 'NotAllowedError') {
          throw new Error('Authentication was cancelled or timed out');
        }
        throw error;
      }

      const verificationRes = await fetch(`${API}/passkeys/verify-authentication`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: asseResp }),
      });
      const verificationData = await verificationRes.json();

      if (verificationData.success && verificationData.loginData) {
        setLogin(verificationData.loginData);
      } else {
        throw new Error(verificationData.error || 'Failed to verify passkey');
      }
    } catch (err) {
      console.error(err);
      sendNotfication(err.message || 'Error logging in with passkey', 'error', 'top-right');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${API}/api/auth/requirements?type=login`);
        const json = await res.json();
        if (json.status && json.data) {
          setAuthConfig(json.data.fields_config || json.data);
        }
      } catch (err) {}
    };
    fetchConfig();
  }, []);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const parseSocketPayload = (payload) => {
    if (!payload) return null;

    if (typeof payload === 'object' && !ArrayBuffer.isView(payload) && !(payload instanceof ArrayBuffer)) {
      return payload;
    }

    if (typeof payload === 'string') {
      try {
        return JSON.parse(payload);
      } catch (error) {
        return null;
      }
    }

    return decode(payload) || null;
  };

  const setLogin = useCallback((data) => {
    if (!data || typeof data !== 'object') {
      setSubmitted(false);
      setDisabled(false);
      setIsLoading(false);
      sendNotfication('Invalid login response. Please try again.', 'error', 'bottom-left');
      return;
    }

    if (data.status === '2fa') {
      Cookies.set("session", data.token, { expires: 1, path: '/' });
      Cookies.set("refreshToken", data.refreshToken, { expires: 30, path: '/' });
      if (data.id) Cookies.set("uid", data.id, { expires: 14, path: '/' });
      else if (data.uid) Cookies.set("uid", data.uid, { expires: 14, path: '/' });
      storage.setKey('token', data.token);
      storage.setKey('refreshToken', data.refreshToken);
      socket.io.opts.query = { auth_token: data.token, user_key: storage.getKey("key") };
      socket.disconnect();
      socket.connect(); // Connect socket with the token
      setShow2faModal(true);
      setIsLoading(false);
      return;
    }

    if (data.status === true) {
      console.log('=== LOGIN SUCCESS - SHOWING LOADER ===');
      sendNotfication('Successfully Login, Please Wait...', 'success', 'top-right');

      // Force the loader to show
      setIsLoading(true);
      setShow(false);
      setShow2faModal(false);

      Cookies.set("session", data.token, { expires: 1, path: '/' }); // 1 day for session cookie, though it's 15m token
      Cookies.set("refreshToken", data.refreshToken, { expires: 30, path: '/' });
      if (data.id) Cookies.set("uid", data.id, { expires: 14, path: '/' });
      else if (data.uid) Cookies.set("uid", data.uid, { expires: 14, path: '/' });
      storage.setKey('logged', true);
      storage.setKey('token', data.token);
      storage.setKey('refreshToken', data.refreshToken);
      storage.setKey('name', data.name);
      storage.setKey('avatar', data.avatar);
      storage.setKey('email', data.email);
      storage.setKey('credit', data.credit);
      storage.setKey('room', data.room);
      storage.setKey('friends', data.friends);
      socket.io.opts.query = { auth_token: data.refreshToken || data.token, user_key: storage.getKey("key") };
      socket.disconnect();
      socket.connect();
      Event.emit("login", data);
      setIsLoading(false);
    } else {
      setSubmitted(false);
      setDisabled(false);
      setIsLoading(false);
      setStatus(false);
      const errMsg = typeof data.status === 'string' ? data.status : (data.message || 'Login failed');
      
      const isBlocked = data.blocked || (typeof data.status === 'string' && data.status.toLowerCase().includes('blocked'));
      if (isBlocked) {
        showAccountBlockedModal(data);
      } else {
        sendNotfication(errMsg, 'error', 'top-right');
      }
    }
  }, []);

  const handleLoginSocket = useCallback((data) => {
    console.log('[Login] LOGIN_USER socket payload', data);
    const parsed = parseSocketPayload(data);
    if (!parsed || typeof parsed !== 'object') {
      sendNotfication('Login response could not be parsed. Please retry.', 'error', 'top-right');
      return;
    }
    setLogin(parsed);
  }, [setLogin]);

  const handleShow = () => {
    setShow(true);
    setEffect('zoomIn');
    // generateCaptcha(); // If generateCaptcha was defined, it should be called here
  };

  const handleClose = () => {
    setShow(false);
    setEffect('zoomOut');
    setDisabled(false);
    setStatus(false);
    setSubmitted(false);
    setOtpValue('');
    setIsOtpSent(false);
  };

  const handleSendOtpLogin = async (overrideType) => {
    const loginIdentifier = email || phone || username;
    if (!loginIdentifier && !overrideType) return sendNotfication(`Please enter your email or phone first.`, 'warning');
    
    let targetIdentifier = loginIdentifier;
    let type = loginIdentifier?.includes('@') ? 'email' : 'phone';
    
    if (overrideType === 'email') {
      if (!email) return sendNotfication(`Please enter your email first.`, 'warning');
      targetIdentifier = email;
      type = 'email';
    } else if (overrideType === 'phone') {
      if (!phone) return sendNotfication(`Please enter your phone first.`, 'warning');
      targetIdentifier = phone;
      type = 'phone';
    }

    setSendingOtp(type);
    try {
      const endpoint = type === 'email' ? '/api/users/send-otp' : '/api/users/send-phone-otp';
      const payload = type === 'email' ? { email: targetIdentifier, context: 'login' } : { phone: targetIdentifier, context: 'login' };
      
      const response = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.status || response.ok) {
        sendNotfication('OTP sent successfully!', 'success');
        setOtpSentTo(type);
        setIsOtpSent(true);
      } else {
        sendNotfication(data.message || 'Error sending OTP', 'error', 'top-right');
      }
    } catch (error) {
      console.error('OTP Send Error:', error);
      sendNotfication('Something went wrong', 'error', 'top-right');
    } finally {
      setSendingOtp(false);
    }
  };

  useEffect(() => {
    socket.on(C.LOGIN_USER, handleLoginSocket);
    const showLoginForm = () => handleShow();
    Event.on('login_form', showLoginForm);

    return () => {
      socket.off(C.LOGIN_USER, handleLoginSocket);
      // Remove the specific listener if Event emitter supports it, otherwise be cautious
      // Event.off('login_form', showLoginForm); 
    };
  }, [handleLoginSocket]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitted(true);
    setDisabled(true);
    setStatus('Please wait');

    // Enforce required fields based on authConfig ONLY if we are not currently verifying an OTP
    if (!isOtpSent) {
      if ((!authConfig || authConfig.email?.visible !== false) && authConfig?.email?.required && !email) {
        setDisabled(false); setStatus(false);
        return sendNotfication('Please enter Email.', 'warning');
      }

      if (authConfig?.phone?.visible && authConfig?.phone?.required && !phone) {
        setDisabled(false); setStatus(false);
        return sendNotfication('Please enter Phone Number.', 'warning');
      }

      if ((!authConfig || authConfig.username?.visible !== false) && authConfig?.username?.required && !username) {
        setDisabled(false); setStatus(false);
        return sendNotfication('Please enter Username.', 'warning');
      }

      if ((!authConfig || authConfig.password?.visible !== false) && authConfig?.password?.required !== false && !password && !otpValue) {
        setDisabled(false); setStatus(false);
        return sendNotfication('Please enter password or OTP.', 'warning');
      }
    }

    let loginIdentifier = email || phone || username;
    if (isOtpSent && otpSentTo) {
      loginIdentifier = otpSentTo === 'email' ? email : phone;
    }

    if (!loginIdentifier) {
      setDisabled(false);
      setStatus(false);
      return sendNotfication('Please enter your login details.', 'warning');
    }

    if (authConfig?.recaptcha?.visible && (!recaptchaToken || recaptchaToken === 'google')) {
      setDisabled(false);
      setStatus(false);
      return sendNotfication('Please solve the reCAPTCHA.', 'warning');
    }

    const isEmail = loginIdentifier.includes('@');
    const verifyRequired = isEmail 
      ? authConfig?.email?.verify === 'login' 
      : authConfig?.phone?.verify === 'login';

    if (verifyRequired && !otpValue) {
      if (!isOtpSent) {
        await handleSendOtpLogin();
        setDisabled(false);
        setStatus(false);
        return;
      } else {
        setDisabled(false);
        setStatus(false);
        return sendNotfication('Please enter OTP.', 'warning');
      }
    }

    socket.emit(C.LOGIN_USER, encode({
      username: loginIdentifier,
      password: password,
      recaptcha: recaptchaToken,
      otp: otpValue
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'username') setUsername(value);
    if (name === 'password') setPassword(value);
    if (name === 'verificationCode') setVerificationCode(value);
  };

  const signup = (e) => {
    e.preventDefault();
    handleClose();
    Event.emit('register_form');
  };

  const handle2FAClose = () => {
    setShow2faModal(false);
    setVerificationCode('');
  };

  const handle2FASubmit = (e) => {
    e.preventDefault();

    const loginIdentifier = email || phone || username;
    // Emit the verification code to the server using LOGIN_USER
    socket.emit(C.LOGIN_USER, encode({
      username: loginIdentifier,
      password: password,
      recaptcha: 'google',
      twoFaCode: verificationCode
    }));

    // We do NOT listen for TWO_FA_CONFIRM here anymore,
    // because setLogin inside useEffect already listens to C.LOGIN_USER.
  };

  console.log('Login render - isLoading:', isLoading);

  return (
    <>
      {isLoading && <CustomLoader show={isLoading} />}
      {props.hideTrigger ? null : (storage.getKey('logged') ? false : (
        <StyledSignInButton onClick={handleShow} className='login-btn' style={{ border: "1px solid gray" }}>
          Sign in
        </StyledSignInButton>
      ))}

      <StyledModal
        size="md"
        centered
        show={storage.getKey('logged') ? false : show}
        onHide={handleClose}
        aria-labelledby="login-modal"
        className={`animated ${effect}`}
      >
            <ModalBody>
              <CloseButton onClick={handleClose}>×</CloseButton>
              <div style={{ marginBottom: '24px' }}>
                <img src={window?.siteConfig?.logo_big ? (API + window.siteConfig.logo_big) : "/assets/images/logo.png"} alt="Logo" style={{ height: '36px', objectFit: 'contain' }} />
              </div>

              <StyledForm onSubmit={handleSubmit}>
                {(!authConfig || authConfig.email?.visible !== false) && (
                  <FieldContainer>
                    <FieldLabel>Email {authConfig?.email?.required && <span style={{color: '#ef4444'}}>*</span>}</FieldLabel>
                    <InputContainer>
                      <StyledInput
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter email"
                      />
                      {authConfig?.email?.verify === 'login' && !isOtpSent && (
                        <OtpButton type="button" onClick={() => handleSendOtpLogin('email')} disabled={sendingOtp === 'email'}>
                          {sendingOtp === 'email' ? 'Sending...' : 'Send OTP'}
                        </OtpButton>
                      )}
                    </InputContainer>
                  </FieldContainer>
                )}

                {(authConfig?.phone?.visible) && (
                  <FieldContainer>
                    <FieldLabel>Phone Number {authConfig?.phone?.required && <span style={{color: '#ef4444'}}>*</span>}</FieldLabel>
                    <InputContainer>
                      <StyledInput
                        type="text"
                        name="phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Enter phone number"
                      />
                      {authConfig?.phone?.verify === 'login' && !isOtpSent && (
                        <OtpButton type="button" onClick={() => handleSendOtpLogin('phone')} disabled={sendingOtp === 'phone'}>
                          {sendingOtp === 'phone' ? 'Sending...' : 'Send OTP'}
                        </OtpButton>
                      )}
                    </InputContainer>
                  </FieldContainer>
                )}

                {(!authConfig || authConfig.username?.visible !== false) && (
                  <FieldContainer>
                    <FieldLabel>Username {authConfig?.username?.required && <span style={{color: '#ef4444'}}>*</span>}</FieldLabel>
                    <StyledInput
                      type="text"
                      name="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Enter username"
                    />
                  </FieldContainer>
                )}

                <FieldContainer>
                  <FieldLabel>Password {authConfig?.password?.required === false ? '(Optional)' : <span style={{color: '#ef4444'}}>*</span>}</FieldLabel>
                  <InputContainer>
                    <StyledInput
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={password}
                      onChange={handleInputChange}
                      placeholder="Enter password"
                    />
                    <EyeIcon type="button" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </EyeIcon>
                  </InputContainer>
                </FieldContainer>

                {authConfig?.password?.required === false && (
                  <FieldContainer>
                    <FieldLabel>Or Login with OTP</FieldLabel>
                    <OtpButton 
                      type="button" 
                      onClick={handleSendOtpLogin} 
                      disabled={sendingOtp} 
                      style={{ width: '100%', marginLeft: 0 }}
                    >
                      {sendingOtp ? 'Sending...' : isOtpSent ? 'Resend OTP' : 'Send OTP to Login'}
                    </OtpButton>
                  </FieldContainer>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '-10px' }}>
                  <Link to="/forgot-password" onClick={handleClose} style={{ color: 'white', fontSize: '14px', fontWeight: '600', textDecoration: 'none' }}>
                    Forgot Password?
                  </Link>
                </div>

                {authConfig?.recaptcha?.visible && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA}
                      theme="dark"
                      onChange={(token) => setRecaptchaToken(token)}
                    />
                  </div>
                )}

                <StyledButton type="submit" disabled={disabled}>
                  {status ? status : 'Sign In'}
                </StyledButton>

                {(!authConfig || authConfig.google?.visible !== false || authConfig.facebook?.visible !== false || authConfig.passkey?.visible !== false || authConfig.kick?.visible !== false) && (
                  <>
                    <StakeDivider>OR</StakeDivider>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '10px' }}>
                      {(!authConfig || authConfig.passkey?.visible !== false) && (
                        <StakeSocialButton type="button" onClick={handlePasskeyLogin}>
                          <Fingerprint size={20} /> Sign In with passkey
                        </StakeSocialButton>
                      )}
                      
                      {(!authConfig || authConfig.google?.visible !== false) && (
                        <StakeSocialButton type="button" onClick={() => {
                          window.location.href = `${API}/api/auth/google`;
                        }}>
                          <img src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" alt="Google" style={{ width: '20px', height: '20px' }} />
                          Sign In with Google
                        </StakeSocialButton>
                      )}

                      {(!authConfig || authConfig.facebook?.visible !== false) && (
                        <StakeSocialButton type="button" onClick={() => {
                          window.location.href = `${API}/api/auth/facebook`;
                        }}>
                          <img src="https://upload.wikimedia.org/wikipedia/commons/5/51/Facebook_f_logo_%282019%29.svg" alt="Facebook" style={{ width: '20px', height: '20px' }} />
                          Sign In with Facebook
                        </StakeSocialButton>
                      )}

                      {(!authConfig || authConfig.kick?.visible !== false) && (
                        <StakeSocialButton type="button" onClick={() => {
                          window.location.href = `${API}/api/auth/kick`;
                        }}>
                          <img src="/assets/bcimages/kick.png" alt="Kick" style={{ width: '20px', height: '20px', filter: 'invert(1)' }} />
                          Sign In with Kick
                        </StakeSocialButton>
                      )}
                    </div>
                  </>
                )}
                
                <div style={{ textAlign: 'center', marginTop: '4px', fontSize: '14px', color: '#9CA3AF' }}>
                  Don't have an account? <span onClick={signup} style={{ color: 'white', fontWeight: '600', cursor: 'pointer' }}>Register</span>
                </div>
              </StyledForm>

            </ModalBody>
      </StyledModal>

      <OtpModalStyled show={isOtpSent} onHide={() => setIsOtpSent(false)} backdrop="static" centered>
        <div style={{ textAlign: 'center' }}>
          <h3 style={{ color: 'white', marginBottom: '16px', fontWeight: 'bold' }}>Enter OTP</h3>
          <p style={{ color: '#aaa', marginBottom: '24px', fontSize: '14px' }}>
            We have sent a 6-digit OTP to your {otpSentTo || 'email/phone'}.
          </p>
          <StyledInput 
            style={{ marginBottom: '24px', textAlign: 'center', letterSpacing: '8px', fontSize: '20px', fontWeight: 'bold' }} 
            type="text" 
            value={otpValue} 
            onChange={(e) => setOtpValue(e.target.value)} 
            placeholder="------" 
            maxLength={6}
          />
          <div style={{ display: 'flex', gap: '12px' }}>
            <CancelButton type="button" onClick={() => setIsOtpSent(false)}>Cancel</CancelButton>
            <VerifyButton type="button" onClick={handleSubmit}>Verify</VerifyButton>
          </div>
        </div>
      </OtpModalStyled>

      <TwoFAModal
        size="sm"
        centered
        show={show2faModal}
        onHide={handle2FAClose}
        aria-labelledby="2fa-modal"
      >
        <ModalBody>
          <CloseButton onClick={handle2FAClose}>×</CloseButton>
          <h4>Enter 2FA Code</h4>
          <StyledForm onSubmit={handle2FASubmit}>
            <StyledInput
              type="text"
              name="verificationCode"
              value={verificationCode}
              onChange={handleInputChange}
              placeholder="Enter 6-digit code"
              maxLength="6"
            />
            <ButtonContainer>
              <SignInButton type="submit">Verify</SignInButton>
            </ButtonContainer>
          </StyledForm>
        </ModalBody>
      </TwoFAModal>
    </>
  );
};


export default Login;
